import './WinnerSelectionTableDemand.scss';
import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ICustomComponentProps } from '@molecules/formbuilder/controls/CustomComponent/ICustomComponentProps';
import { IOffer, ITableDemandItem } from '@models/winnerSelection/ITableDemand';
import Hint from '@srm/ui/hint';
import AuroraLoader from '@srm/ui/aurora-loader';

import OfferHeaderMenuDialog from '../OfferHeaderMenuDialog';
import { useTableDemandData, IWinnerSelectionTableDemandInfo } from '../hooks/useTableDemandData';
import { useWinnerTableScroll } from '../hooks/useWinnerTableScroll';
import { useGroupedCategories } from '../hooks/useGroupedCategories';
import { useOfferModals } from '../hooks/useOfferModals';
import { useTableViewMode } from '../hooks/useTableViewMode';
import { useFontScale } from '../hooks/useFontScale';
import { useComparisonFields } from '../hooks/useComparisonFields';
import { checkIsPriceWinner, checkIsTotalWinner } from '../utils/winnerLogic';
import { buildComparisonBenchmarks } from '../utils/comparisonFields';
import { CategoryRow } from '../parts/CategoryRow';
import { CritCell } from '../parts/CritCell';
import { OfferHeader } from '../parts/OfferHeader';
import { OfferCell } from '../parts/OfferCell';
import { OfferActionModals } from '../parts/OfferActionModals';
import { HorizontalPager } from '../parts/HorizontalPager';
import { ViewModeToggle } from '../parts/ViewModeToggle';
import { ComparisonFieldsPicker, type IComparisonBaseField } from '../parts/ComparisonFieldsPicker';
import { buildOfferMenuActions } from '../parts/buildOfferMenuActions';

export type { IWinnerSelectionTableDemandInfo };

export type IWinnerSelectionTableDemandProps<TFieldValues extends object = object> =
    ICustomComponentProps<TFieldValues> & {
        /** Колбэк после загрузки данных таблицы: info + docId позиции, для
         *  которой info актуальна. docId передаётся вторым аргументом, чтобы
         *  parent правильно ассоциировал кэш по ключу позиции (race-protect
         *  при быстром переключении: ответ старой позиции мог прийти позже
         *  смены currentPosition, и closure на currentPosition.key зафиксировал бы
         *  не ту позицию). */
        onDataLoaded?: (info: IWinnerSelectionTableDemandInfo, loadedDocId: string) => void;
        /** true — таблица встроена в TablePositions: --ws-font-scale на свой корень
         *  НЕ ставим, чтобы не перебить значение, унаследованное от обёртки позиций
         *  (.ws-positions-wrapper). В standalone-режиме (false) таблица сама
         *  выставляет --ws-font-scale (масштаб берётся из пользовательских настроек
         *  в навбаре). Сам контрол размера живёт в навбаре, не в таблице. */
        embedded?: boolean;
    };

const WinnerSelectionTableDemand = <TFieldValues extends object = object>({
    component,
    docId,
    onDataLoaded,
    embedded,
}: IWinnerSelectionTableDemandProps<TFieldValues>) => {
    const tableKey = component.params?.tableKey;

    /* ─── Данные таблицы + сброс модалок при смене docId ─── */
    const {
        data: tableData,
        isLoading,
        isStale,
        errorText,
        reload,
    } = useTableDemandData(docId, tableKey, onDataLoaded);
    const { data: items, offers, settings } = tableData ?? {};
    const modals = useOfferModals(docId);

    /* ─── Ориентация таблицы (персист localStorage) ─── */
    const [view, setView] = useTableViewMode();

    /* ─── Дополнительные поля сравнения (опциональны в конфиге). ─── */
    const comparisonFields = useMemo(() => settings?.comparisonFields ?? [], [settings?.comparisonFields]);
    const { selectedKeys, selectedFields, maxSelected, updateSelectedKeys } = useComparisonFields(
        tableKey,
        comparisonFields,
        settings?.comparisonFieldsMaxSelected,
    );

    /* ─── Масштаб шрифта: читаем значение (управляется из навбара) и в standalone
     *  выставляем --ws-font-scale на корень. Во встроенном режиме переменную
     *  задаёт обёртка позиций — здесь не дублируем (см. embedded). ─── */
    const fontScale = useFontScale();

    /* ─── Горизонтальный скролл ─── */
    const { scrollRef, canScrollLeft, canScrollRight, columnWidth, firstColWidth, scrollByPage } = useWinnerTableScroll(
        view,
        tableData,
    );

    /* ─── Группировка критериев ─── */
    const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
    const toggleCat = useCallback((name: string) => {
        setCollapsedCats((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    }, []);
    const groupedCategories = useGroupedCategories(items, settings?.categorySortOrder);
    /* Плоский список критериев в порядке категорий — столбцы транспонированного вида. */
    const orderedCrits = useMemo(() => groupedCategories.flatMap((cat) => cat.crits), [groupedCategories]);

    /* ─── Winner-helpers (мемо чтобы избежать пересоздания при ре-рендере) ─── */
    const offersList = useMemo(() => offers ?? [], [offers]);
    const isTotalWinner = useCallback(
        (key: string) => checkIsTotalWinner(offersList, key, settings?.totalWinnerFactor),
        [offersList, settings?.totalWinnerFactor],
    );
    /* ─── Лидер по цене считается по рублёвой цене сравнения (comparePrice),
     *  независимо от валюты предложения — см. checkIsPriceWinner. ─── */
    const isPriceWinner = useCallback((key: string) => checkIsPriceWinner(offersList, key), [offersList]);

    /* ─── Стат-метрики для OfferHeader (мин. цена сравнения + макс. summary) ─── */

    const minComparePrice = useMemo<number | null>(() => {
        const valid = offersList.filter((o) => !o.isWithdrawn && o.comparePrice != null);
        if (valid.length === 0) return null;
        return Math.min(...valid.map((o) => o.comparePrice as number));
    }, [offersList]);

    const maxSummary = useMemo<number | null>(() => {
        if (offersList.length === 0) return null;
        const valid = offersList.filter((o) => !o.isWithdrawn && o.summaryScore != null);
        if (valid.length === 0) return null;
        return Math.max(...valid.map((o) => o.summaryScore as number));
    }, [offersList]);

    const comparisonBenchmarks = useMemo(
        () => buildComparisonBenchmarks(selectedFields, offersList),
        [selectedFields, offersList],
    );

    /* ─── Render: loading / error / empty ─── */
    if (isLoading && !tableData) {
        return (
            <div className="ws-demand-wrapper">
                {errorText ? (
                    <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
                ) : null}
                <div className="ws-demand__loading">
                    <AuroraLoader label="Загрузка" />
                </div>
            </div>
        );
    }

    if (items === undefined || offers === undefined || settings === undefined) {
        return (
            <div className="ws-demand-wrapper">
                {errorText ? (
                    <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
                ) : null}
            </div>
        );
    }

    if (offers.length === 0) {
        return (
            <div className="ws-demand-wrapper">
                <div className="ws-demand__empty">
                    <h1>{settings.emptyTextHeader}</h1>
                    <p>{settings.emptyText}</p>
                </div>
            </div>
        );
    }

    /* ─── Переиспользуемые ячейки (одинаковы в обоих видах) ─── */
    const renderOfferHeader = (offer: IOffer) => (
        <OfferHeaderMenuDialog list={buildOfferMenuActions(offer, tableKey!, modals.open)}>
            <OfferHeader
                offer={offer}
                settings={settings}
                isTotalWinner={isTotalWinner(offer.key)}
                isPriceWinner={isPriceWinner(offer.key)}
                minComparePrice={minComparePrice}
                maxSummary={maxSummary}
                comparisonFields={selectedFields}
                comparisonBenchmarks={comparisonBenchmarks}
                onSelectWinner={(selectedOffer, action) => modals.open('rate', selectedOffer, action)}
            />
        </OfferHeaderMenuDialog>
    );

    const renderOfferCell = (crit: ITableDemandItem, offer: IOffer) => (
        <OfferCell
            crit={crit}
            offer={offer}
            settings={settings}
            demandId={docId}
            tableKey={tableKey!}
            onReload={reload}
        />
    );

    const baseComparisonFields: IComparisonBaseField[] = [
        ...(settings.priceVisible
            ? [
                  {
                      key: 'price',
                      title: 'Цена',
                      description: 'Отображаемая цена предложения в валюте участника',
                      comparison: 'min' as const,
                  },
              ]
            : []),
        ...(!settings.hideSummary
            ? [
                  {
                      key: 'rating',
                      title: 'Рейтинг',
                      description: 'Итоговая оценка по весам критериев',
                      comparison: 'max' as const,
                  },
              ]
            : []),
    ];

    /* Угловая ячейка: подпись первого столбца + переключатель ориентации.
     * Селектор полностью отсутствует, если ComparisonFields не настроены. */
    const cornerCell = (
        <th className="ws-th ws-th-crit" {...(view === 'byOffer' ? { rowSpan: 2 } : {})}>
            <div className="ws-corner">
                <div className="ws-corner__top">
                    <span className="ws-corner__label">{view === 'byCriteria' ? 'Критерий' : 'Предложение'}</span>
                    <ViewModeToggle value={view} onChange={setView} />
                </div>
                {comparisonFields.length > 0 ? (
                    <ComparisonFieldsPicker
                        fields={comparisonFields}
                        selectedKeys={selectedKeys}
                        maxSelected={maxSelected}
                        baseFields={baseComparisonFields}
                        onChange={updateSelectedKeys}
                    />
                ) : null}
            </div>
        </th>
    );

    const columnsCount = view === 'byCriteria' ? offers.length : orderedCrits.length;

    /* ─── Main render ─── */
    return (
        <div
            className={clsx('ws-demand-wrapper', { 'is-refreshing': isStale })}
            style={embedded ? undefined : ({ '--ws-font-scale': fontScale.scale } as React.CSSProperties)}
        >
            {errorText ? (
                <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
            ) : null}

            <div className="ws-scroll" ref={scrollRef}>
                <table className={clsx('ws-table', { 'ws-table--by-offer': view === 'byOffer' })} role="grid">
                    {view === 'byCriteria' ? (
                        <>
                            <colgroup>
                                <col className="ws-col-crit" />
                                {offers.map((o) => (
                                    <col key={o.key} className="ws-col-offer" />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    {cornerCell}
                                    {offers.map((offer) => (
                                        <th key={offer.key} className="ws-th ws-th-offer">
                                            {renderOfferHeader(offer)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {groupedCategories.map((cat) => {
                                    const isCollapsed = collapsedCats.has(cat.name);
                                    return (
                                        <React.Fragment key={cat.name}>
                                            <CategoryRow
                                                name={cat.name}
                                                totalWeight={cat.totalWeight}
                                                isCollapsed={isCollapsed}
                                                colSpan={offers.length + 1}
                                                onToggle={toggleCat}
                                            />
                                            {!isCollapsed &&
                                                cat.crits.map((crit: ITableDemandItem, i) => (
                                                    <tr
                                                        key={crit.critId}
                                                        className={clsx('ws-row', { 'ws-row--alt': i % 2 === 1 })}
                                                    >
                                                        <td className="ws-td ws-td-crit">
                                                            <CritCell crit={crit} settings={settings} />
                                                        </td>
                                                        {offers.map((offer: IOffer) => (
                                                            <td key={offer.key} className="ws-td">
                                                                {renderOfferCell(crit, offer)}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </>
                    ) : (
                        <>
                            <colgroup>
                                <col className="ws-col-offerhead" />
                                {orderedCrits.map((crit) => (
                                    <col key={crit.critId} className="ws-col-critcol" />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    {cornerCell}
                                    {groupedCategories.map((cat) => (
                                        <th key={cat.name} className="ws-th ws-th-catgroup" colSpan={cat.crits.length}>
                                            <div className="ws-catgroup">
                                                <span className="ws-catgroup__name" title={cat.name}>
                                                    Категория <b>{cat.name}</b>
                                                </span>
                                                {cat.totalWeight != null && (
                                                    <span
                                                        className="ws-catgroup__sum"
                                                        title="Суммарный вес критериев категории"
                                                    >
                                                        вес{' '}
                                                        <span className="ws-catgroup__sigma" aria-hidden>
                                                            ∑
                                                        </span>{' '}
                                                        <b>
                                                            {cat.totalWeight.toLocaleString('ru-RU', {
                                                                maximumFractionDigits: 4,
                                                            })}
                                                        </b>
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                                <tr>
                                    {orderedCrits.map((crit) => (
                                        <th key={crit.critId} className="ws-th ws-th-critcol">
                                            <CritCell crit={crit} settings={settings} />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {offers.map((offer) => (
                                    <tr key={offer.key} className="ws-row">
                                        <td className="ws-td ws-td-offerhead">{renderOfferHeader(offer)}</td>
                                        {orderedCrits.map((crit) => (
                                            <td key={crit.critId} className="ws-td">
                                                {renderOfferCell(crit, offer)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </>
                    )}
                </table>
            </div>

            <HorizontalPager
                columnsCount={columnsCount}
                columnWidth={columnWidth}
                firstColWidth={firstColWidth}
                scrollRef={scrollRef}
                canScrollLeft={canScrollLeft}
                canScrollRight={canScrollRight}
                onScroll={scrollByPage}
            />

            <OfferActionModals
                tableKey={tableKey!}
                demandId={docId}
                offer={modals.offerToEdit}
                action={modals.currentAction}
                activeModal={modals.activeModal}
                onClose={modals.close}
                onReload={reload}
                settings={settings}
            />
        </div>
    );
};

export default WinnerSelectionTableDemand;
